import React, { useState, useMemo } from 'react';
import Icon from 'components/AppIcon';
import { AUDIT_TRAIL, AUDIT_ACTION_TYPES, AUDIT_ACTION_COLORS } from './teamData';

export default function AuditTrailTab({ showToast }) {
  const [actionFilter, setActionFilter] = useState('All');
  const [memberFilter, setMemberFilter] = useState('All');
  const [dateRange, setDateRange] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedRow, setExpandedRow] = useState(null);
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 10;

  const memberNames = useMemo(() => ['All', ...new Set(AUDIT_TRAIL.map(a => a.actor))], []);

  const filtered = useMemo(() => {
    let list = [...AUDIT_TRAIL];
    if (actionFilter !== 'All') list = list?.filter(a => a?.actionType === actionFilter);
    if (memberFilter !== 'All') list = list?.filter(a => a?.actor === memberFilter);
    if (dateRange === '24h') list = list?.filter(a => Date.now() - a?.timestamp?.getTime() < 86400000);
    else if (dateRange === '7d') list = list?.filter(a => Date.now() - a?.timestamp?.getTime() < 7 * 86400000);
    else if (dateRange === '30d') list = list?.filter(a => Date.now() - a?.timestamp?.getTime() < 30 * 86400000);
    if (searchQuery?.trim()) {
      const q = searchQuery?.toLowerCase();
      list = list?.filter(
        (a) =>
          a?.actor?.toLowerCase()?.includes(q) ||
          a?.target?.toLowerCase()?.includes(q) ||
          a?.description?.toLowerCase()?.includes(q)
      );
    }
    return list;
  }, [actionFilter, memberFilter, dateRange, searchQuery]);

  const totalPages = Math.ceil(filtered?.length / PAGE_SIZE);
  const paginated = filtered?.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const formatTs = (ts) => ts?.toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });

  return (
    <div>
      {/* Filter Bar */}
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="relative">
          <Icon name="Search" size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 pointer-events-none text-muted-foreground" />
          <input
            type="text"
            placeholder="Search audit log…"
            value={searchQuery}
            onChange={(e) => { setSearchQuery(e?.target?.value); setPage(1); }}
            className="pl-8 pr-3 py-1.5 border border-border rounded-lg font-caption text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 bg-card text-foreground w-48"
          />
        </div>
        <div className="flex items-center gap-2">
          <label className="font-caption text-xs font-medium text-muted-foreground">Action Type</label>
          <select value={actionFilter} onChange={e => { setActionFilter(e?.target?.value); setPage(1); }} className="border border-border rounded-lg px-2 py-1.5 font-caption text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 bg-card text-foreground">
            {AUDIT_ACTION_TYPES?.map(t => <option key={t}>{t}</option>)}
          </select>
        </div>
        <div className="flex items-center gap-2">
          <label className="font-caption text-xs font-medium text-muted-foreground">Member</label>
          <select value={memberFilter} onChange={e => { setMemberFilter(e?.target?.value); setPage(1); }} className="border border-border rounded-lg px-2 py-1.5 font-caption text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 bg-card text-foreground">
            {memberNames?.map(m => <option key={m}>{m}</option>)}
          </select>
        </div>
        <div className="flex items-center gap-2">
          <label className="font-caption text-xs font-medium text-muted-foreground">Date Range</label>
          <select value={dateRange} onChange={e => { setDateRange(e?.target?.value); setPage(1); }} className="border border-border rounded-lg px-2 py-1.5 font-caption text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 bg-card text-foreground">
            <option value="all">All Time</option>
            <option value="24h">Last 24 Hours</option>
            <option value="7d">Last 7 Days</option>
            <option value="30d">Last 30 Days</option>
          </select>
        </div>
        <button
          onClick={() => showToast('Audit log exported as CSV')}
          className="ml-auto flex items-center gap-2 px-3 py-1.5 rounded-lg font-caption text-sm font-medium border border-border text-muted-foreground hover:bg-muted transition-colors"
        >
          <Icon name="Download" size={14} />
          Export Audit Log
        </button>
      </div>
      {/* Table */}
      <div className="bg-card rounded-2xl border border-border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[900px]">
            <thead>
              <tr className="bg-muted border-b border-border">
                {['Timestamp', 'Actor', 'Action Type', 'Target', 'Description', 'IP Address', '']?.map(col => (
                  <th key={col} className="px-4 py-3 text-left font-caption text-xs font-semibold text-muted-foreground uppercase tracking-wide">{col}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-border/50">
              {paginated?.map(entry => (
                <React.Fragment key={entry?.id}>
                  <tr className="hover:bg-muted transition-colors">
                    <td className="px-4 py-3 font-caption text-xs text-muted-foreground whitespace-nowrap">{formatTs(entry?.timestamp)}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <div
                          className="w-6 h-6 rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0"
                          style={{ background: 'var(--color-primary)' }}
                        >
                          {entry?.actorInitials}
                        </div>
                        <span className="font-caption text-sm text-foreground font-medium">{entry?.actor}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full font-caption text-xs font-semibold ${AUDIT_ACTION_COLORS?.[entry?.actionType] || 'bg-muted text-muted-foreground'}`}>
                        {entry?.actionType}
                      </span>
                    </td>
                    <td className="px-4 py-3 font-caption text-sm text-muted-foreground max-w-[120px] truncate">{entry?.target}</td>
                    <td className="px-4 py-3 font-caption text-sm text-foreground max-w-[260px]">
                      <span className="line-clamp-2">{entry?.description}</span>
                    </td>
                    <td className="px-4 py-3 font-caption text-xs text-muted-foreground font-mono">{entry?.ip}</td>
                    <td className="px-4 py-3">
                      <button
                        onClick={() => setExpandedRow(expandedRow === entry?.id ? null : entry?.id)}
                        className="p-1 rounded hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
                        title="View details"
                      >
                        <Icon name={expandedRow === entry?.id ? 'ChevronUp' : 'ChevronDown'} size={15} />
                      </button>
                    </td>
                  </tr>
                  {expandedRow === entry?.id && (
                    <tr className="bg-muted">
                      <td colSpan={7} className="px-6 py-3">
                        <div className="bg-card rounded-xl border border-border p-4">
                          <p className="font-caption text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">Event Details</p>
                          <pre className="font-caption text-xs text-foreground font-mono whitespace-pre-wrap">{JSON.stringify(entry?.details, null, 2)}</pre>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
              {paginated?.length === 0 && (
                <tr><td colSpan={7} className="px-4 py-12 text-center font-caption text-muted-foreground text-sm">No audit entries match the current filters.</td></tr>
              )}
            </tbody>
          </table>
        </div>
        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-border">
            <p className="font-caption text-sm text-muted-foreground">Showing {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, filtered?.length)} of {filtered?.length}</p>
            <div className="flex gap-1">
              <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} className="px-3 py-1.5 rounded-lg font-caption text-sm border border-border disabled:opacity-40 hover:bg-muted">Previous</button>
              {Array.from({ length: totalPages }, (_, i) => i + 1)?.map(p => (
                <button key={p} onClick={() => setPage(p)} className={`px-3 py-1.5 rounded-lg font-caption text-sm border ${p === page ? 'border-primary bg-primary/8 text-primary font-semibold' : 'border-border hover:bg-muted'}`}>{p}</button>
              ))}
              <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages} className="px-3 py-1.5 rounded-lg font-caption text-sm border border-border disabled:opacity-40 hover:bg-muted">Next</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
